'use client';

import { Outlet, useParams } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';

import { useSessionClaims } from '@/hooks/use-session-claims';
import { usePartnerAdmin } from '@/hooks/use-partner-admin';

/**
 * TagadaRx desk shell (`/rx`, `/rx/:partner`).
 *
 * Deliberately chromeless: the desk is worked all day by support staff, so
 * the page title, counters and Inbox / Mailboxes switch rendered by
 * `RxSupportDesk` are the whole header — no backoffice banner, no tab bar
 * of unrelated admin tools. This is not an admin surface either: rx_ops /
 * rx_admin are support roles (Tagada's TGD Care team or a partner's staff),
 * which is why the desk has its own namespace rather than a tab in `/admin`.
 *
 * Gate (enforced once here, mirrored server-side by `rxOpsProcedure`):
 * - `/rx` — superadmin or unscoped Rx staff.
 * - `/rx/:partner` — superadmin, unscoped Rx staff, or Rx staff scoped to
 *   that partner (`usePartnerAdmin().canViewRx`).
 */
export default function RxLayout() {
  const { partner } = useParams<{ partner?: string }>();
  const { isSuperAdmin, isRxStaff, whitelabelScope, isLoaded, isSignedIn } = useSessionClaims();
  const partnerAccess = usePartnerAdmin(partner);

  if (!isLoaded || (partner && partnerAccess.isLoading)) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-muted-foreground text-sm">Loading...</div>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Denied title="Sign in required" body="Sign in to open the TagadaRx desk." />;
  }

  const canOpen = partner ? partnerAccess.canViewRx : isSuperAdmin || (isRxStaff && !whitelabelScope);

  if (!canOpen) {
    return (
      <Denied
        title="Access Denied"
        body={
          partner
            ? `The TagadaRx desk for ${partner} is restricted to its Rx staff.`
            : 'The TagadaRx desk is restricted to Rx staff (rx_ops / rx_admin).'
        }
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl">
      <Outlet />
    </div>
  );
}

function Denied({ title, body }: { title: string; body: string }) {
  return (
    <div className="bg-background flex min-h-[60vh] items-center justify-center">
      <div className="text-center">
        <ShieldAlert className="text-muted-foreground mx-auto h-12 w-12" />
        <h2 className="mt-4 text-xl font-semibold">{title}</h2>
        <p className="text-muted-foreground mt-2 max-w-md">{body}</p>
      </div>
    </div>
  );
}
